/**
 =========================================================
 * Material Dashboard 2 React - v2.2.0
 =========================================================

 * Product Page: https://www.creative-tim.com/product/material-dashboard-react
 * Copyright 2023 Creative Tim (https://www.creative-tim.com)

 Coded by www.creative-tim.com

 =========================================================

 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 */

import * as React from 'react';

// @mui material components
import Grid from "@mui/material/Grid";

import MDInput from "../../../components/MDInput";
import PropTypes from "prop-types";
import { useFormContext } from "react-hook-form";
import {FormControl, FormControlLabel, InputLabel, MenuItem, Select, Switch} from "@mui/material";
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ClearIcon from '@mui/icons-material/Clear';
import MDTypography from "../../../components/MDTypography";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import useApi from "../../../components/Utils/api";
import toast from "react-hot-toast";
import {useEffect, useState, useRef} from "react";
import {useTranslation} from "react-i18next";
import useAllowOnlyNumber from "../../../hooks/useAllowOnlyNumber";
import useAllowOnlyDecimal from "../../../hooks/useAllowOnlyDecimal";
import useAllowOnlyAlphanumeric from "../../../hooks/useAllowOnlyAlphanumeric";

const DOC_SLOTS = 3;

const generateDocName = (slotIndex) => {
  const now = new Date();
  const date =
    String(now.getFullYear()) +
    String(now.getMonth() + 1).padStart(2, "0") +
    String(now.getDate()).padStart(2, "0");
  const time =
    String(now.getHours()).padStart(2, "0") +
    String(now.getMinutes()).padStart(2, "0") +
    String(now.getSeconds()).padStart(2, "0");
  const slot = String(slotIndex + 1).padStart(3, "0");
  return `SUPP_${slot}_${date}${time}`;
};

function MerchantOtherDetails({merchant, editmode, setMerchant, type}) {
  const {t} = useTranslation();
  const { register, formState: { errors }, setValue } = useFormContext();
  const api = useApi();
  const handleNumberKeyDown = useAllowOnlyNumber();
  const handleDecimalKeyDown = useAllowOnlyDecimal();
  const handleAlphanumericKeyDown = useAllowOnlyAlphanumeric();
  const [files, setFiles] = useState(merchant.merchantFiles?.length > 0 ? merchant.merchantFiles : []);
  const [cardProcessors, setCardProcessors] = useState([]);
  const [walletProcessors, setWalletProcessors] = useState([]);
  const [selectedCardProcessor, setSelectedCardProcessor] = useState("");
  const [selectedWalletProcessors, setSelectedWalletProcessors] = useState([]);
  const [threeDSecureEnabled, setThreeDSecureEnabled] = useState(false);
  const [saveCardEnabled, setSaveCardEnabled] = useState(false);
  const [applePayEnabled, setApplePayEnabled] = useState(false);
  const [samsungPayEnabled, setSamsungPayEnabled] = useState(false);
  // Saved on GATEWAY_MERCHANT; Payment Gateway also reveals the gateway settings below
  const [paymentGatewayEnabled, setPaymentGatewayEnabled] = useState(false);
  const [posEnabled, setPosEnabled] = useState(false);
  // Botim merchant-specific PEM keys. Never returned by the backend, so these are
  // always blank in edit mode — blank means "keep the existing stored keys".
  const [botimPrivateKey, setBotimPrivateKey] = useState("");
  const [botimPublicKey, setBotimPublicKey] = useState("");

  // 3 independent upload slots: each entry is { file: File, displayName: string } or null
  const [docSlots, setDocSlots] = useState([null, null, null]);
  const fileInputRefs = useRef([null, null, null]);

  // Merchant logo — single image shown on the customer-facing payment invoice
  const [logoFile, setLogoFile] = useState(null);
  const [existingLogoUrl, setExistingLogoUrl] = useState(null);
  const logoInputRef = useRef(null);

  // Load the already-uploaded logo (if any) so edit mode shows the current one
  useEffect(() => {
    if (!merchant?.merchantLogo?.id) {
      setExistingLogoUrl(null);
      return;
    }
    api
      .get("merchant/file/" + merchant.merchantLogo.id, { responseType: "blob" })
      .then((response) => {
        const blob = new Blob([response.data], { type: response.headers["content-type"] });
        setExistingLogoUrl(URL.createObjectURL(blob));
      })
      .catch(() => {
        setExistingLogoUrl(null);
      });
  }, [merchant?.merchantLogo?.id]);

  const handleLogoSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!["image/png", "image/jpeg"].includes(file.type)) {
      toast.error(t("merchant_logo_invalid_type"));
      event.target.value = "";
      return;
    }
    setLogoFile(file);
    setValue("merchantLogo", file);
    event.target.value = "";
  };

  const handleLogoClear = () => {
    setLogoFile(null);
    setValue("merchantLogo", undefined);
  };

  useEffect(() => {
    api
      .get("processors")
      .then((response) => {
        const list = Array.isArray(response.data?.body)
          ? response.data.body
          : Array.isArray(response.data)
            ? response.data
            : [];
        setCardProcessors(list.filter((p) => p.processorCategory === "CARD"));
        setWalletProcessors(list.filter((p) => p.processorCategory === "WALLET"));
      })
      .catch((error) => {
        console.error("Error fetching processors:", error);
      });
  }, []);

  useEffect(() => {
    if (merchant?.merchantGatewayObject) {
      // Card processor is single-select; take the first value if legacy data has several
      setSelectedCardProcessor(
        merchant.merchantGatewayObject.cardProcessorType
          ? merchant.merchantGatewayObject.cardProcessorType.split(",").filter(Boolean)[0] || ""
          : ""
      );
      setSelectedWalletProcessors(
        merchant.merchantGatewayObject.enabledWalletTypes
          ? merchant.merchantGatewayObject.enabledWalletTypes.split(",").filter(Boolean)
          : []
      );
      setThreeDSecureEnabled(!!merchant.merchantGatewayObject.threeDSecureEnabled);
      setSaveCardEnabled(!!merchant.merchantGatewayObject.saveCardEnabled);
      setApplePayEnabled(!!merchant.merchantGatewayObject.applePayEnabled);
      setSamsungPayEnabled(!!merchant.merchantGatewayObject.samsungPayEnabled);
      setPaymentGatewayEnabled(!!merchant.merchantGatewayObject.paymentGatewayEnabled);
      setPosEnabled(!!merchant.merchantGatewayObject.posEnabled);
    }
  }, [merchant?.merchantGatewayObject]);

  const handleCardProcessorChange = (event) => {
    const { value } = event.target;
    setSelectedCardProcessor(value);
    setMerchant((prev) => ({
      ...prev,
      merchantGatewayObject: {
        ...prev.merchantGatewayObject,
        cardProcessorType: value,
      },
    }));
  };

  const handleBotimKeyChange = (field, setLocal) => (event) => {
    const { value } = event.target;
    setLocal(value);
    setMerchant((prev) => ({
      ...prev,
      merchantGatewayObject: {
        ...prev.merchantGatewayObject,
        [field]: value,
      },
    }));
  };

  const handleWalletProcessorChange = (event) => {
    const { value } = event.target;
    const next = typeof value === "string" ? value.split(",") : value;
    setSelectedWalletProcessors(next);
    setMerchant((prev) => ({
      ...prev,
      merchantGatewayObject: {
        ...prev.merchantGatewayObject,
        enabledWalletTypes: next.join(","),
      },
    }));
  };

  const handleThreeDSecureChange = (event) => {
    const { checked } = event.target;
    setThreeDSecureEnabled(checked);
    setMerchant((prev) => ({
      ...prev,
      merchantGatewayObject: {
        ...prev.merchantGatewayObject,
        threeDSecureEnabled: checked,
      },
    }));
  };

  const handleSaveCardChange = (event) => {
    const { checked } = event.target;
    setSaveCardEnabled(checked);
    setMerchant((prev) => ({
      ...prev,
      merchantGatewayObject: {
        ...prev.merchantGatewayObject,
        saveCardEnabled: checked,
      },
    }));
  };

  const handlePaymentGatewayChange = (event) => {
    const { checked } = event.target;
    setPaymentGatewayEnabled(checked);
    // Turning the gateway off clears its settings so nothing hidden is submitted
    if (!checked) {
      setThreeDSecureEnabled(false);
      setSaveCardEnabled(false);
      setApplePayEnabled(false);
      setSamsungPayEnabled(false);
      setBotimPrivateKey("");
      setBotimPublicKey("");
    }
    setMerchant((prev) => ({
      ...prev,
      merchantGatewayObject: {
        ...prev.merchantGatewayObject,
        paymentGatewayEnabled: checked,
        ...(!checked && {
          threeDSecureEnabled: false,
          saveCardEnabled: false,
          applePayEnabled: false,
          samsungPayEnabled: false,
          botimPrivateKey: "",
          botimPublicKey: "",
        }),
      },
    }));
  };

  const handlePosChange = (event) => {
    const { checked } = event.target;
    setPosEnabled(checked);
    setMerchant((prev) => ({
      ...prev,
      merchantGatewayObject: {
        ...prev.merchantGatewayObject,
        posEnabled: checked,
      },
    }));
  };

  const handleApplePayChange = (event) => {
    const { checked } = event.target;
    setApplePayEnabled(checked);
    setMerchant((prev) => ({
      ...prev,
      merchantGatewayObject: {
        ...prev.merchantGatewayObject,
        applePayEnabled: checked,
      },
    }));
  };

  const handleSamsungPayChange = (event) => {
    const { checked } = event.target;
    setSamsungPayEnabled(checked);
    setMerchant((prev) => ({
      ...prev,
      merchantGatewayObject: {
        ...prev.merchantGatewayObject,
        samsungPayEnabled: checked,
      },
    }));
  };

  const syncDocSlotsToForm = (nextSlots) => {
    const filled = nextSlots.filter(Boolean).map((slot) => {
      const renamed = new File([slot.file], slot.displayName, { type: slot.file.type });
      return [renamed];
    });
    setValue("supportingDocuments", filled.length > 0 ? filled : undefined, { shouldValidate: true });
  };

  const handleDocSelect = (slotIndex, event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const displayName = generateDocName(slotIndex);
    setDocSlots((prev) => {
      const next = [...prev];
      next[slotIndex] = { file, displayName };
      syncDocSlotsToForm(next);
      return next;
    });
    // reset the input so selecting same file again still fires onChange
    event.target.value = "";
  };

  const handleDocClear = (slotIndex) => {
    setDocSlots((prev) => {
      const next = [...prev];
      next[slotIndex] = null;
      syncDocSlotsToForm(next);
      return next;
    });
  };

  const downloadFile = (file) => {
    api.get("merchant/file/"+file.id, {
      responseType: 'blob'
    })
    .then(response => {
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    })
    .catch(error => {
      console.log(error);
    });
  }

  const deleteFile = (file) => {
    api.delete("merchant/file/"+file.id)
        .then(response => {
          if (response.data.statusCodeValue === 200) {
            const fileIdToDelete = file.id;
            setFiles(prevFiles => prevFiles.filter(file => file.id !== fileIdToDelete));
            toast.success(t('file')+": "+file.fileName+" "+t('deleted_successfully'));
          }
        })
        .catch(error => {
          console.log(error);
        });
  }

  const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: 'left',
    color: theme.palette.text.secondary,
    fontSize: '0.7rem',
    verticalAlign: 'middle',
    display: 'inline-flex'
  }));

  return (
      <Grid mt={3} mb={3} sx={{ maxWidth: "680px" }}>
        <Grid container spacing={2}>
          {/* Hidden per request (still registered so submitted data is unaffected) */}
          <Grid item xs={12} sm={6} sx={{ display: "none" }}>
            <MDInput type="text" label={t('max_transaction_amount')} name="amountPerTrans"
                     error={errors?.amountPerTrans}
                     helperText={errors.amountPerTrans ? errors.amountPerTrans.message : ''}
                     onKeyDown={handleNumberKeyDown}
                     {...register("amountPerTrans")}
                     fullWidth  disabled={!editmode} />
          </Grid>
          <Grid item xs={12} sm={6} sx={{ display: "none" }}>
            <MDInput type="text" label={t('max_total_value_per_day')} name="allowedTotAmtPerDay"
                     error={errors?.allowedTotAmtPerDay}
                     helperText={errors.allowedTotAmtPerDay ? errors.allowedTotAmtPerDay.message : ''}
                     onKeyDown={handleNumberKeyDown}
                     {...register("allowedTotAmtPerDay")}
                     fullWidth  disabled={!editmode} />
          </Grid>
          <Grid item xs={12} sm={6} sx={{ display: "none" }}>
            <MDInput type="text" label={t('max_no_transactions_per_day')} name="allowedTransPerDay"
                     error={errors?.allowedTransPerDay}
                     helperText={errors.allowedTransPerDay ? errors.allowedTransPerDay.message : ''}
                     onKeyDown={handleNumberKeyDown}
                     {...register("allowedTransPerDay")}
                     fullWidth  disabled={!editmode} />
          </Grid>
          <Grid item xs={12} sm={6} sx={{ display: "none" }}>
            <MDInput type="text" label={t('bank_fee')} name="bankFee"
                     error={errors?.bankFee}
                     helperText={errors.bankFee ? errors.bankFee.message : ''}
                     onKeyDown={handleDecimalKeyDown}
                     {...register("bankFee")}
                     fullWidth  disabled={!editmode} />
          </Grid>
          <Grid item xs={12} sm={6} sx={{ display: "none" }}>
            <MDInput type="text" label={t('comission_fee')} name="issuerFee"
                     error={errors?.issuerFee}
                     helperText={errors.issuerFee ? errors.issuerFee.message : ''}
                     onKeyDown={handleDecimalKeyDown}
                     {...register("issuerFee")}
                     fullWidth  disabled={!editmode} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required={type === "new"} disabled={!editmode}>
              <InputLabel id="card-processor-label">{t("enabled_card_processors")}</InputLabel>
              <Select
                labelId="card-processor-label"
                value={selectedCardProcessor}
                onChange={handleCardProcessorChange}
                label={t("enabled_card_processors")}
                sx={{ height: "44px" }}
              >
                <MenuItem value="">
                  <em>{t("none")}</em>
                </MenuItem>
                {cardProcessors.map((p) => (
                  <MenuItem key={p.processorType} value={p.processorType} disabled={!p.active}>
                    {p.processorType}{!p.active ? ` (${t("inactive")})` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {/* Merchant ID: required + editable when creating a new merchant;
              read-only (existing value) when updating one. */}
          <Grid item xs={12} sm={6}>
            <MDInput type="text"
                     label={type === "new" ? t('merchant_id') : t('transactionSummarycolumns.cardAcceptorCode')}
                     name="mid"
                     error={errors?.mid}
                     required={type === "new"}
                     helperText={errors.mid ? errors.mid.message : ''}
                     onKeyDown={handleAlphanumericKeyDown}
                     {...register("mid")}
                     fullWidth  disabled={type === "update" || !editmode} />
          </Grid>
          {/* Hidden per request (still wired up so the saved data is unaffected) */}
          <Grid item xs={12} sm={6} sx={{ display: "none" }}>
            <FormControl fullWidth disabled={!editmode}>
              <InputLabel id="wallet-processor-label">{t("enabled_wallet_processors")}</InputLabel>
              <Select
                labelId="wallet-processor-label"
                multiple
                value={selectedWalletProcessors}
                onChange={handleWalletProcessorChange}
                label={t("enabled_wallet_processors")}
                sx={{ height: "44px" }}
              >
                {walletProcessors.map((p) => (
                  <MenuItem key={p.processorType} value={p.processorType} disabled={!p.active}>
                    {p.processorType}{!p.active ? ` (${t("inactive")})` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} display="flex" alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={paymentGatewayEnabled}
                  onChange={handlePaymentGatewayChange}
                  disabled={!editmode}
                />
              }
              label={t("payment_gateway")}
            />
          </Grid>
          <Grid item xs={12} sm={6} display="flex" alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={posEnabled}
                  onChange={handlePosChange}
                  disabled={!editmode}
                />
              }
              label={t("pos")}
            />
          </Grid>

          {/* Payment Gateway settings */}
          {paymentGatewayEnabled && (
          <>
          <Grid item xs={12} sm={6} display="flex" alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={threeDSecureEnabled}
                  onChange={handleThreeDSecureChange}
                  disabled={!editmode}
                />
              }
              label={t("enable_3d_secure")}
            />
          </Grid>
          <Grid item xs={12} sm={6} display="flex" alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={saveCardEnabled}
                  onChange={handleSaveCardChange}
                  disabled={!editmode}
                />
              }
              label={t("enable_save_card")}
            />
          </Grid>
          <Grid item xs={12} sm={6} display="flex" alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={applePayEnabled}
                  onChange={handleApplePayChange}
                  disabled={!editmode}
                />
              }
              label={t("enable_apple_pay")}
            />
          </Grid>
          <Grid item xs={12} sm={6} display="flex" alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={samsungPayEnabled}
                  onChange={handleSamsungPayChange}
                  disabled={!editmode}
                />
              }
              label={t("enable_samsung_pay")}
            />
          </Grid>

          {/* Botim merchant-specific credentials — the MID is the Botim partner id */}
              <Grid item xs={6}>
                <MDInput
                  type="text"
                  label="Botim Private Key (PEM)"
                  name="botimPrivateKey"
                  multiline
                  rows={4}
                  value={botimPrivateKey}
                  onChange={handleBotimKeyChange("botimPrivateKey", setBotimPrivateKey)}
                  required={type === "new" && selectedCardProcessor === "BOTIM"}
                  helperText={type === "new"
                    ? "Required for BOTIM. Merchant-specific Botim signing key. The Merchant ID must be the Botim partner id."
                    : "Leave blank to keep the existing stored key."}
                  fullWidth
                  disabled={!editmode}
                />
              </Grid>
              <Grid item xs={6}>
                <MDInput
                  type="text"
                  label="Botim Public Key (PEM)"
                  name="botimPublicKey"
                  multiline
                  rows={4}
                  value={botimPublicKey}
                  onChange={handleBotimKeyChange("botimPublicKey", setBotimPublicKey)}
                  required={type === "new" && selectedCardProcessor === "BOTIM"}
                  helperText={type === "new"
                    ? "Required for BOTIM. Botim public key for this partner id."
                    : "Leave blank to keep the existing stored key."}
                  fullWidth
                  disabled={!editmode}
                />
              </Grid>
          </>
          )}

          {/* Merchant Logo — shown on the payment invoice */}
          <Grid item xs={12} sm={6}>
            <MDTypography variant="button" fontWeight="medium" color="text" mb={1} display="block">
              {t('merchant_logo')}
            </MDTypography>
            <input
              type="file"
              accept="image/png,image/jpeg"
              style={{ display: "none" }}
              ref={logoInputRef}
              onChange={handleLogoSelect}
            />
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Box
                  onClick={() => editmode && !logoFile && logoInputRef.current?.click()}
                  sx={{
                    border: "1.5px dashed",
                    borderColor: logoFile || existingLogoUrl ? "success.main" : "grey.400",
                    borderRadius: 2,
                    p: 2,
                    height: 110,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: editmode && !logoFile ? "pointer" : "default",
                    bgcolor: logoFile ? "success.light" : "grey.50",
                    opacity: !editmode ? 0.6 : 1,
                    position: "relative",
                    transition: "border-color 0.2s, background-color 0.2s",
                    "&:hover": editmode && !logoFile ? { borderColor: "info.main", bgcolor: "info.light" } : {},
                  }}
                >
                  {logoFile ? (
                    <>
                      <img
                        src={URL.createObjectURL(logoFile)}
                        alt="Merchant logo preview"
                        style={{ maxHeight: 60, maxWidth: "80%", objectFit: "contain" }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, wordBreak: "break-all", textAlign: "center" }}>
                        {logoFile.name}
                      </Typography>
                      {editmode && (
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); handleLogoClear(); }}
                          sx={{ position: "absolute", top: 4, right: 4, color: "error.main" }}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      )}
                    </>
                  ) : existingLogoUrl ? (
                    <>
                      <img
                        src={existingLogoUrl}
                        alt="Current merchant logo"
                        style={{ maxHeight: 60, maxWidth: "80%", objectFit: "contain" }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, textAlign: "center" }}>
                        {editmode ? t('current_logo_click_to_replace') : t('current_logo')}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <CloudUploadIcon sx={{ color: "grey.400", mb: 0.5 }} />
                      <Typography variant="caption" color="text.secondary" textAlign="center">
                        {t('merchant_logo')}
                      </Typography>
                      <Typography variant="caption" color="text.disabled" textAlign="center">
                        PNG, JPG
                      </Typography>
                    </>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Grid>

          {/* Supporting Documents */}
          <Grid item xs={12} sm={6}>
            <MDTypography variant="button" fontWeight="medium" color="text" mb={1} display="block">
              {t('supporting_documents')}
            </MDTypography>
            {errors?.supportingDocuments && (
              <MDTypography variant="caption" color="error" display="block" mb={1}>
                {errors.supportingDocuments.message || errors.supportingDocuments[0]?.message}
              </MDTypography>
            )}
            <Grid container spacing={2}>
              {Array.from({ length: DOC_SLOTS }, (_, i) => {
                const slot = docSlots[i];
                return (
                  <Grid item xs={4} key={i}>
                    <input
                      type="file"
                      accept=".pdf,.png,.jpeg,.jpg,.xlsx"
                      style={{ display: "none" }}
                      ref={(el) => { fileInputRefs.current[i] = el; }}
                      onChange={(e) => handleDocSelect(i, e)}
                    />
                    <Box
                      onClick={() => editmode && !slot && fileInputRefs.current[i]?.click()}
                      sx={{
                        border: "1.5px dashed",
                        borderColor: errors?.supportingDocuments ? "error.main" : slot ? "success.main" : "grey.400",
                        borderRadius: 2,
                        p: 2,
                        height: 110,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: editmode && !slot ? "pointer" : "default",
                        bgcolor: slot ? "success.light" : "grey.50",
                        opacity: !editmode ? 0.6 : 1,
                        position: "relative",
                        transition: "border-color 0.2s, background-color 0.2s",
                        "&:hover": editmode && !slot ? { borderColor: "info.main", bgcolor: "info.light" } : {},
                      }}
                    >
                      {slot ? (
                        <>
                          <AttachFileIcon sx={{ color: "success.dark", mb: 0.5 }} />
                          <Typography
                            variant="caption"
                            fontWeight="bold"
                            color="success.dark"
                            sx={{ wordBreak: "break-all", textAlign: "center", fontFamily: "monospace" }}
                          >
                            {slot.displayName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                            {(slot.file.size / 1024).toFixed(1)} KB
                          </Typography>
                          {editmode && (
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); handleDocClear(i); }}
                              sx={{ position: "absolute", top: 4, right: 4, color: "error.main" }}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          )}
                        </>
                      ) : (
                        <>
                          <CloudUploadIcon sx={{ color: "grey.400", mb: 0.5 }} />
                          <Typography variant="caption" color="text.secondary" textAlign="center">
                            {t('supporting_documents')} {i + 1}
                          </Typography>
                          <Typography variant="caption" color="text.disabled" textAlign="center">
                            PDF, PNG, JPG, XLSX
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>

            {/* Already-uploaded files */}
            {files.length > 0 && (
              <Box mt={2}>
                <MDTypography variant="caption" color="text" display="block" mb={1}>
                  {t('uploaded_files') || "Uploaded files"}
                </MDTypography>
                <Stack spacing={1}>
                  {files.map((file, index) => (
                    <Item key={index}>
                      <AttachFileIcon sx={{ fontSize: '1rem !important' }} />
                      <Typography fontSize={14} style={{ width: '80%' }}>{file.fileName}</Typography>
                      <DownloadIcon sx={{ fontSize: '1rem !important', float: 'right', cursor: 'pointer' }} onClick={() => downloadFile(file)} />
                      <DeleteIcon sx={{ fontSize: '1rem !important', float: 'right', cursor: editmode ? 'pointer' : 'not-allowed' }} onClick={editmode ? () => deleteFile(file) : null} />
                    </Item>
                  ))}
                </Stack>
              </Box>
            )}
          </Grid>

        </Grid>
      </Grid>
  );
}

MerchantOtherDetails.defaultProps = {
  merchant: false,
  editmode: false,
  setMerchant: false,
  type: ""
}

MerchantOtherDetails.propTypes = {
  merchant: PropTypes.any,
  editmode: PropTypes.bool,
  setMerchant: PropTypes.func,
  type: PropTypes.string
}

export default MerchantOtherDetails;
